import React, { useState, useEffect } from 'react';
import { groupCallService, CallParticipant } from '../services/groupCallService';
import { getAvatarUrl } from '../utils/avatarUtils';

interface GroupCallInviteModalProps {
  currentUserId: string;
  currentUserName: string;
  currentUserPhoto?: string;
  onStartCall: (participants: CallParticipant[], callType: 'audio' | 'video') => void;
  onClose: () => void;
}

const GroupCallInviteModal: React.FC<GroupCallInviteModalProps> = ({
  currentUserId,
  currentUserName,
  currentUserPhoto,
  onStartCall,
  onClose
}) => {
  const [connections, setConnections] = useState<CallParticipant[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [callType, setCallType] = useState<'audio' | 'video'>('video');

  // Load user's connections
  useEffect(() => {
    const loadConnections = async () => {
      try {
        setLoading(true);
        const conns = await groupCallService.getUserConnections(currentUserId);
        setConnections(conns);
      } catch (error) {
        console.error('Error loading connections:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConnections();
  }, [currentUserId]);

  const toggleParticipant = (uid: string) => {
    setSelectedParticipants(prev => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  const filteredConnections = connections.filter(c =>
    c.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleStartCall = () => {
    const selected = connections.filter(c => selectedParticipants.has(c.uid));
    if (selected.length === 0) return;
    onStartCall(selected, callType);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #f0f7f5 0%, #e8f4f0 100%)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold" style={{ color: '#3C4F49' }}>Gesprek starten</h2>
              <p className="text-sm mt-0.5" style={{ color: '#7A9488' }}>Nodig connecties uit</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors"
              style={{ color: '#7A9488' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          {/* Call type toggle */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setCallType('audio')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                callType === 'audio'
                  ? 'bg-white shadow-sm'
                  : 'text-gray-500 hover:bg-white/40'
              }`}
              style={callType === 'audio' ? { color: '#8DA8A0', border: '1px solid #8DA8A0' } : {}}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
              Audio
            </button>
            <button
              onClick={() => setCallType('video')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                callType === 'video'
                  ? 'bg-white shadow-sm'
                  : 'text-gray-500 hover:bg-white/40'
              }`}
              style={callType === 'video' ? { color: '#2563EB', border: '1px solid #2563EB' } : {}}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
              Video
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-gray-100">
          <input
            type="text"
            placeholder="Zoek connectie..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
            style={{ '--tw-ring-color': '#8DA8A0' } as React.CSSProperties}
          />
        </div>

        {/* Selected participants preview */}
        {selectedParticipants.size > 0 && (
          <div className="px-4 py-2 bg-orange-50 border-b border-orange-100">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-orange-600 font-medium">Geselecteerd:</span>
              {connections
                .filter(c => selectedParticipants.has(c.uid))
                .map(c => (
                  <span
                    key={c.uid}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-full text-xs text-gray-700 border border-orange-200"
                  >
                    {c.displayName}
                    <button
                      onClick={() => toggleParticipant(c.uid)}
                      className="text-gray-400 hover:text-red-500 ml-0.5"
                    >
                      ✕
                    </button>
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Connections list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : filteredConnections.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-2" style={{ fontSize: '40px' }}>—</div>
              <p className="text-sm">
                {searchQuery
                  ? 'Geen connecties gevonden'
                  : 'Je hebt nog geen connecties'}
              </p>
            </div>
          ) : (
            filteredConnections.map((connection) => {
              const isSelected = selectedParticipants.has(connection.uid);

              return (
                <div
                  key={connection.uid}
                  onClick={() => toggleParticipant(connection.uid)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-gray-50 ${
                    isSelected
                      ? 'bg-orange-50 hover:bg-orange-100'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Checkbox */}
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    isSelected
                      ? 'bg-orange-500 border-orange-500'
                      : 'border-gray-300'
                  }`}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>

                  {/* Avatar */}
                  <img
                    src={getAvatarUrl(connection.photoURL || '', connection.uid, connection.displayName, '4F6660')}
                    alt={connection.displayName}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {connection.displayName}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {selectedParticipants.size} van {connections.length} geselecteerd
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleStartCall}
                disabled={selectedParticipants.size === 0}
                className="px-5 py-2 text-sm font-medium rounded-lg transition-colors"
              style={selectedParticipants.size > 0
                ? { background: callType === 'video' ? '#2563EB' : '#8DA8A0', color: '#fff' }
                : { background: '#E5E7EB', color: '#9CA3AF', cursor: 'not-allowed' }}
              >
                Start {callType === 'video' ? 'videogesprek' : 'audiogesprek'}
                {selectedParticipants.size > 0 && ` (${selectedParticipants.size + 1})`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupCallInviteModal;
